"use client";
import { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Slider,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  GridView as GridIcon,
  TrendingUp as TrendingUpIcon,
  Calculate as CalculateIcon,
} from "@mui/icons-material";
import { palette, theme as colorTheme } from "../../Convoze.theme";
import ParentWrapper from "../../components/ParentWrapper";
import Header from "../../components/header";

const primary = colorTheme('palette', 'primary').value;
const positive = colorTheme('palette', 'positive').value;
const neutral = colorTheme('palette', 'neutral').value;
const negative = colorTheme('palette', 'negative').value;

const DarkContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  backgroundColor: "#1a1a1a",
  color: "white",
  padding: theme.spacing(4),
}));

const SectionCard = styled(Paper)(({ theme, variant }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  backgroundColor: variant === "current" ? "#2a2a3a" : "#1E2C2D",
  border: variant === "current" ? `1px solid ${primary}` : `1px solid ${positive}`,
  color: "white",
  height: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const SectionHeader = styled(Box)(({ theme, variant }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),

  "& .section-icon": {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: variant === "current" ? "#E0E1FD" : `${positive}`,
    color: "white",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: "#333",
  color: "white",
  borderRadius: 8,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#555",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#777",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: primary,
  },
  "& .MuiSelect-icon": {
    color: "white",
  },
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const StyledSlider = styled(Slider)(({ theme }) => ({
  color: primary,
  height: 8,
  "& .MuiSlider-track": {
    border: "none",
    backgroundColor: primary,
  },
  "& .MuiSlider-rail": {
    backgroundColor: "#555",
  },
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: primary,
    border: "2px solid #fff",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: "0 0 0 8px rgba(74, 108, 247, 0.16)",
    },
  },
}));

const CalculateButton = styled(Button)(({ theme }) => ({
  backgroundColor: positive,
  color: "#000",
  fontWeight: "bold",
  fontSize: "1.1rem",
  padding: theme.spacing(2, 4),
  borderRadius: 12,
  textTransform: "none",
  width: "100%",
  "&:hover": {
    backgroundColor: positive,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
    padding: theme.spacing(1.5, 3),
  },
}));

const SavingsPlaceholder = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 300,
  textAlign: "center",
  gap: theme.spacing(3),
  "& .calculator-icon": {
    fontSize: 80,
    color: "#555",
    opacity: 0.5,
  },
}));

function Subscription() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [currentTool, setCurrentTool] = useState("WordPress");
  const [projectsPerMonth, setProjectsPerMonth] = useState(3);
  const [hourlyRate, setHourlyRate] = useState(75);
  const [teamSize, setTeamSize] = useState(2);
  const [showResults, setShowResults] = useState(false);

  const handleCalculate = () => {
    setShowResults(true);
  };

  const formatProjectsValue = (value) => {
    if (value >= 20) return "20+";
    return value.toString();
  };

  const formatRateValue = (value) => {
    if (value >= 200) return "$200+";
    return `$${value}`;
  };

  const formatTeamValue = (value) => {
    if (value >= 10) return "10+";
    if (value === 1) return "1";
    return `${value} people`;
  };

  return (
    // <DarkContainer>
    //   <Container
    //     sx={{
    //       height: "100%",
    //       display: "flex",
    //       flexDirection: "column",
    //       gap: 4,
    //     }}
    //   >
    //     {/* Header */}
    //     <Box sx={{ textAlign: "center" }}>
    //       <Typography
    //         variant={isMobile ? "h3" : "h2"}
    //         sx={{ fontSize: "3rem" }}
    //         component="h1"
    //         fontWeight="bold"
    //         color="white"
    //         gutterBottom
    //       >
    //         Calculate Your ROI
    //       </Typography>
    //       <Typography
    //         variant={isMobile ? "body1" : "h6"}
    //         color="#aaa"
    //         sx={{ mx: "auto", lineHeight: 1.6, fontSize: "1rem" }}
    //       >
    //         See how much time and money you could save by switching to Rollout.
    //         <br />
    //         Most customers see ROI within their first month.
    //       </Typography>
    //     </Box>

    //     {/* Main Content */}
    //     <Grid container spacing={4}>
    //       {/* Current Situation */}
    //       <Grid item xs={12} md={6}>
    //         <SectionCard variant="current" elevation={0}>
    //           <SectionHeader variant="current">
    //             <Box className="section-icon">
    //               {/* <GridIcon /> */}
    //               <svg
    //                 xmlns="http://www.w3.org/2000/svg"
    //                 width="24"
    //                 height="24"
    //                 viewBox="0 0 24 24"
    //                 fill="none"
    //                 style={{ color: primary}}
    //                 stroke="currentColor"
    //                 stroke-width="2"
    //                 stroke-linecap="round"
    //                 stroke-linejoin="round"
    //                 class="lucide lucide-calculator w-6 h-6 text-blue-400"
    //               >
    //                 <rect width="16" height="20" x="4" y="2" rx="2"></rect>
    //                 <line x1="8" x2="16" y1="6" y2="6"></line>
    //                 <line x1="16" x2="16" y1="14" y2="18"></line>
    //                 <path d="M16 10h.01"></path>
    //                 <path d="M12 10h.01"></path>
    //                 <path d="M8 10h.01"></path>
    //                 <path d="M12 14h.01"></path>
    //                 <path d="M8 14h.01"></path>
    //                 <path d="M12 18h.01"></path>
    //                 <path d="M8 18h.01"></path>
    //               </svg>
    //             </Box>
    //             <Typography variant="h5" fontWeight="bold" color="white">
    //               Your Current Situation
    //             </Typography>
    //           </SectionHeader>

    //           {/* Website Building Method */}
    //           <Box sx={{}}>
    //             <Typography variant="body1" color="#ccc" gutterBottom>
    //               How do you currently build websites?
    //             </Typography>
    //             <FormControl fullWidth>
    //               <StyledSelect
    //                 value={currentTool}
    //                 onChange={(e) => setCurrentTool(e.target.value)}
    //               >
    //                 <MenuItem value="WordPress">WordPress</MenuItem>
    //                 <MenuItem value="Custom HTML/CSS">Custom HTML/CSS</MenuItem>
    //                 <MenuItem value="React">React</MenuItem>
    //                 <MenuItem value="Vue">Vue</MenuItem>
    //                 <MenuItem value="Other CMS">Other CMS</MenuItem>
    //               </StyledSelect>
    //             </FormControl>
    //           </Box>

    //           {/* Projects per month */}
    //           <SliderContainer>
    //             <Typography variant="body1" color="#ccc" gutterBottom>
    //               Projects per month
    //             </Typography>
    //             <StyledSlider
    //               value={projectsPerMonth}
    //               onChange={(e, newValue) => setProjectsPerMonth(newValue)}
    //               min={1}
    //               max={20}
    //               step={1}
    //             />
    //             <Box
    //               sx={{
    //                 display: "flex",
    //                 justifyContent: "space-between",
    //                 mt: 1,
    //               }}
    //             >
    //               <Typography variant="body2" color="#888">
    //                 1
    //               </Typography>
    //               <Typography variant="body2" color="white" fontWeight="bold">
    //                 {formatProjectsValue(projectsPerMonth)}
    //               </Typography>
    //               <Typography variant="body2" color="#888">
    //                 20+
    //               </Typography>
    //             </Box>
    //           </SliderContainer>

    //           {/* Hourly Rate */}
    //           <SliderContainer>
    //             <Typography variant="body1" color="#ccc" gutterBottom>
    //               Your hourly rate ($)
    //             </Typography>
    //             <StyledSlider
    //               value={hourlyRate}
    //               onChange={(e, newValue) => setHourlyRate(newValue)}
    //               min={25}
    //               max={200}
    //               step={5}
    //             />
    //             <Box
    //               sx={{
    //                 display: "flex",
    //                 justifyContent: "space-between",
    //                 mt: 1,
    //               }}
    //             >
    //               <Typography variant="body2" color="#888">
    //                 $25
    //               </Typography>
    //               <Typography variant="body2" color="white" fontWeight="bold">
    //                 {formatRateValue(hourlyRate)}
    //               </Typography>
    //               <Typography variant="body2" color="#888">
    //                 $200+
    //               </Typography>
    //             </Box>
    //           </SliderContainer>

    //           {/* Team Size */}
    //           <SliderContainer>
    //             <Typography variant="body1" color="#ccc" gutterBottom>
    //               Team size
    //             </Typography>
    //             <StyledSlider
    //               value={teamSize}
    //               onChange={(e, newValue) => setTeamSize(newValue)}
    //               min={1}
    //               max={10}
    //               step={1}
    //             />
    //             <Box
    //               sx={{
    //                 display: "flex",
    //                 justifyContent: "space-between",
    //                 mt: 1,
    //               }}
    //             >
    //               <Typography variant="body2" color="#888">
    //                 1
    //               </Typography>
    //               <Typography variant="body2" color="white" fontWeight="bold">
    //                 {formatTeamValue(teamSize)}
    //               </Typography>
    //               <Typography variant="body2" color="#888">
    //                 10+
    //               </Typography>
    //             </Box>
    //           </SliderContainer>

    //           {/* Calculate Button */}
    //           <CalculateButton onClick={handleCalculate}>
    //             Calculate My ROI
    //           </CalculateButton>
    //         </SectionCard>
    //       </Grid>

    //       {/* Potential Savings */}
    //       <Grid item xs={12} md={6}>
    //         <SectionCard variant="savings" elevation={0}>
    //           <SectionHeader variant="savings">
    //             <Box className="section-icon">
    //               <TrendingUpIcon sx={{ color: positive }} />
    //             </Box>
    //             <Typography variant="h5" fontWeight="bold" color="white">
    //               Your Potential Savings
    //             </Typography>
    //           </SectionHeader>

    //           {!showResults ? (
    //             <SavingsPlaceholder>
    //               {/* <CalculateIcon className="calculator-icon" /> */}
    //               <Box sx={{width:50,height:50,backgroundColor:positive}}>
    //                 <svg
    //                   xmlns="http://www.w3.org/2000/svg"
    //                   width="64"
    //                   height="64"
    //                   viewBox="0 0 24 24"
    //                   fill="none"
    //                   style={{ color: "#555" }}
    //                   stroke="currentColor"
    //                   stroke-width="2"
    //                   stroke-linecap="round"
    //                   stroke-linejoin="round"
    //                   class="lucide lucide-calculator w-6 h-6 text-blue-400"
    //                 >
    //                   <rect width="16" height="20" x="4" y="2" rx="2"></rect>
    //                   <line x1="8" x2="16" y1="6" y2="6"></line>
    //                   <line x1="16" x2="16" y1="14" y2="18"></line>
    //                   <path d="M16 10h.01"></path>
    //                   <path d="M12 10h.01"></path>
    //                   <path d="M8 10h.01"></path>
    //                   <path d="M12 14h.01"></path>
    //                   <path d="M8 14h.01"></path>
    //                   <path d="M12 18h.01"></path>
    //                   <path d="M8 18h.01"></path>
    //                 </svg>
    //               </Box>

    //               <Typography
    //                 variant="body1"
    //                 color="#aaa"
    //                 sx={{ maxWidth: 300 }}
    //               >
    //                 Fill out the form and click "Calculate My ROI" to see your
    //                 potential savings
    //               </Typography>
    //             </SavingsPlaceholder>
    //           ) : (
    //             <Box sx={{ textAlign: "center", py: 4 }}>
    //               <Typography
    //                 variant="h3"
    //                 color={positive}
    //                 fontWeight="bold"
    //                 gutterBottom
    //               >
    //                 $
    //                 {(
    //                   projectsPerMonth *
    //                   hourlyRate *
    //                   teamSize *
    //                   0.3
    //                 ).toLocaleString()}
    //               </Typography>
    //               <Typography variant="h6" color="white" gutterBottom>
    //                 Monthly Savings
    //               </Typography>
    //               <Typography
    //                 variant="body1"
    //                 color="#aaa"
    //                 sx={{ mt: 3, mb: 2 }}
    //               >
    //                 Based on your current situation, you could save:
    //               </Typography>
    //               <Box sx={{ textAlign: "left", maxWidth: 300, mx: "auto" }}>
    //                 <Typography variant="body2" color="#ccc" gutterBottom>
    //                   • {Math.round(projectsPerMonth * teamSize * 8)} hours per
    //                   month
    //                 </Typography>
    //                 <Typography variant="body2" color="#ccc" gutterBottom>
    //                   • $
    //                   {(
    //                     projectsPerMonth *
    //                     hourlyRate *
    //                     teamSize *
    //                     3.6
    //                   ).toLocaleString()}{" "}
    //                   annually
    //                 </Typography>
    //                 <Typography variant="body2" color="#ccc">
    //                   • 30% faster project delivery
    //                 </Typography>
    //               </Box>
    //             </Box>
    //           )}
    //         </SectionCard>
    //       </Grid>
    //     </Grid>
    //   </Container>
    // </DarkContainer>
    <ParentWrapper>
        <DarkContainer>
      <Container
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center" }}>
          {/* <Typography
            variant={isMobile ? "h3" : "h2"}
            sx={{ fontSize: "3rem" }}
            component="h1"
            fontWeight="bold"
            color="white"
            gutterBottom
          >
            Calculate Your ROI
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "h6"}
            color="#aaa"
            sx={{ mx: "auto", lineHeight: 1.6, fontSize: "1rem" }}
          >
            See how much time and money you could save by switching to Rollout.
            <br />
            Most customers see ROI within their first month.
          </Typography> */}

           <Header title={"Calculate Your ROI"} subHeading={"Most customers see ROI within their first month"} />

        </Box>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Current Situation */}
          <Grid item xs={12} md={6}>
            <SectionCard variant="current" elevation={0}>
              <SectionHeader variant="current">
                <Box className="section-icon">
                  {/* <GridIcon /> */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ color: primary}}
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-calculator w-6 h-6 text-blue-400"
                  >
                    <rect width="16" height="20" x="4" y="2" rx="2"></rect>
                    <line x1="8" x2="16" y1="6" y2="6"></line>
                    <line x1="16" x2="16" y1="14" y2="18"></line>
                    <path d="M16 10h.01"></path>
                    <path d="M12 10h.01"></path>
                    <path d="M8 10h.01"></path>
                    <path d="M12 14h.01"></path>
                    <path d="M8 14h.01"></path>
                    <path d="M12 18h.01"></path>
                    <path d="M8 18h.01"></path>
                  </svg>
                </Box>
                <Typography variant="h5" fontWeight="bold" color="white">
                  Your Current Situation
                </Typography>
              </SectionHeader>

              {/* Website Building Method */}
              <Box sx={{}}>
                <Typography variant="body1" color="#ccc" gutterBottom>
                  How do you currently build websites?
                </Typography>
                <FormControl fullWidth>
                  <StyledSelect
                    value={currentTool}
                    onChange={(e) => setCurrentTool(e.target.value)}
                  >
                    <MenuItem value="WordPress">WordPress</MenuItem>
                    <MenuItem value="Custom HTML/CSS">Custom HTML/CSS</MenuItem>
                    <MenuItem value="React">React</MenuItem>
                    <MenuItem value="Vue">Vue</MenuItem>
                    <MenuItem value="Other CMS">Other CMS</MenuItem>
                  </StyledSelect>
                </FormControl>
              </Box>

              {/* Projects per month */}
              <SliderContainer>
                <Typography variant="body1" color="#ccc" gutterBottom>
                  Projects per month
                </Typography>
                <StyledSlider
                  value={projectsPerMonth}
                  onChange={(e, newValue) => setProjectsPerMonth(newValue)}
                  min={1}
                  max={20}
                  step={1}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="body2" color="#888">
                    1
                  </Typography>
                  <Typography variant="body2" color="white" fontWeight="bold">
                    {formatProjectsValue(projectsPerMonth)}
                  </Typography>
                  <Typography variant="body2" color="#888">
                    20+
                  </Typography>
                </Box>
              </SliderContainer>

              {/* Hourly Rate */}
              <SliderContainer>
                <Typography variant="body1" color="#ccc" gutterBottom>
                  Your hourly rate ($)
                </Typography>
                <StyledSlider
                  value={hourlyRate}
                  onChange={(e, newValue) => setHourlyRate(newValue)}
                  min={25}
                  max={200}
                  step={5}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="body2" color="#888">
                    $25
                  </Typography>
                  <Typography variant="body2" color="white" fontWeight="bold">
                    {formatRateValue(hourlyRate)}
                  </Typography>
                  <Typography variant="body2" color="#888">
                    $200+
                  </Typography>
                </Box>
              </SliderContainer>

              {/* Team Size */}
              <SliderContainer>
                <Typography variant="body1" color="#ccc" gutterBottom>
                  Team size
                </Typography>
                <StyledSlider
                  value={teamSize}
                  onChange={(e, newValue) => setTeamSize(newValue)}
                  min={1}
                  max={10}
                  step={1}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="body2" color="#888">
                    1
                  </Typography>
                  <Typography variant="body2" color="white" fontWeight="bold">
                    {formatTeamValue(teamSize)}
                  </Typography>
                  <Typography variant="body2" color="#888">
                    10+
                  </Typography>
                </Box>
              </SliderContainer>

              {/* Calculate Button */}
              <CalculateButton onClick={handleCalculate}>
                Calculate My ROI
              </CalculateButton>
            </SectionCard>
          </Grid>

          {/* Potential Savings */}
          <Grid item xs={12} md={6}>
            <SectionCard variant="savings" elevation={0}>
              <SectionHeader variant="savings">
                <Box className="section-icon">
                  <TrendingUpIcon sx={{ color: positive }} />
                </Box>
                <Typography variant="h5" fontWeight="bold" color="white">
                  Your Potential Savings
                </Typography>
              </SectionHeader>

              {!showResults ? (
                <SavingsPlaceholder>
                  {/* <CalculateIcon className="calculator-icon" /> */}
                  <Box sx={{width:50,height:50,backgroundColor:positive}}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ color: "#555" }}
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-calculator w-6 h-6 text-blue-400"
                    >
                      <rect width="16" height="20" x="4" y="2" rx="2"></rect>
                      <line x1="8" x2="16" y1="6" y2="6"></line>
                      <line x1="16" x2="16" y1="14" y2="18"></line>
                      <path d="M16 10h.01"></path>
                      <path d="M12 10h.01"></path>
                      <path d="M8 10h.01"></path>
                      <path d="M12 14h.01"></path>
                      <path d="M8 14h.01"></path>
                      <path d="M12 18h.01"></path>
                      <path d="M8 18h.01"></path>
                    </svg>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#aaa"
                    sx={{ maxWidth: 300 }}
                  >
                    Fill out the form and click "Calculate My ROI" to see your
                    potential savings
                  </Typography>
                </SavingsPlaceholder>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography
                    variant="h3"
                    color={positive}
                    fontWeight="bold"
                    gutterBottom
                  >
                    $
                    {(
                      projectsPerMonth *
                      hourlyRate *
                      teamSize *
                      0.3
                    ).toLocaleString()}
                  </Typography>
                  <Typography variant="h6" color="white" gutterBottom>
                    Monthly Savings
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#aaa"
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Based on your current situation, you could save:
                  </Typography>
                  <Box sx={{ textAlign: "left", maxWidth: 300, mx: "auto" }}>
                    <Typography variant="body2" color="#ccc" gutterBottom>
                      • {Math.round(projectsPerMonth * teamSize * 8)} hours per
                      month
                    </Typography>
                    <Typography variant="body2" color="#ccc" gutterBottom>
                      • $
                      {(
                        projectsPerMonth *
                        hourlyRate *
                        teamSize *
                        3.6
                      ).toLocaleString()}{" "}
                      annually
                    </Typography>
                    <Typography variant="body2" color="#ccc">
                      • 30% faster project delivery
                    </Typography>
                  </Box>
                </Box>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      </Container>
    </DarkContainer>
    </ParentWrapper>
  );
}

export default Subscription;
